import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Download, CreditCard, Check, X, Upload, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import CheckoutDialog from "@/components/CheckoutDialog";

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }) : "—");

const STATUS_DE = { paid: "Bezahlt", unpaid: "Offen", open: "Offen", overdue: "Überfällig", sent: "Gesendet", accepted: "Angenommen", rejected: "Abgelehnt", pending: "Ausstehend", awaiting_payment: "Zahlung ausstehend", cancelled: "Storniert", refunded: "Erstattet", expired: "Abgelaufen", draft: "Entwurf", converted: "Umgewandelt", in_progress: "In Bearbeitung", planning: "Planung", completed: "Abgeschlossen", on_hold: "Pausiert" };
const stLabel = (s, de) => (de ? (STATUS_DE[(s || "").toLowerCase()] || s) : s);

const badge = (s) => {
  const map = { paid: "bg-green-500/15 text-green-500", unpaid: "bg-amber-500/15 text-amber-500", sent: "bg-blue-500/15 text-blue-500", accepted: "bg-green-500/15 text-green-500", rejected: "bg-red-500/15 text-red-500", pending: "bg-amber-500/15 text-amber-500", awaiting_payment: "bg-amber-500/15 text-amber-500" };
  return map[s] || "bg-secondary text-muted-foreground";
};

export default function PortalList({ type }) {
  const { t, lang, pickL } = useI18n();
  const [items, setItems] = useState([]);
  const [checkoutInv, setCheckoutInv] = useState(null);
  const fileRef = useRef();

  const endpoint = { offers: "/offers", orders: "/orders", projects: "/projects", invoices: "/invoices", files: "/files" }[type];
  const title = { offers: t("portal.offers"), orders: t("portal.orders"), projects: t("portal.projects"), invoices: t("portal.invoices"), files: t("portal.files") }[type];
  const load = useCallback(() => api.get(endpoint).then((r) => setItems(r.data)).catch(() => {}), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const respond = async (id, decision) => {
    try { await api.post(`/offers/${id}/respond`, { decision }); toast.success("OK"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const pay = (inv) => setCheckoutInv(inv);
  const DELETABLE = ["unpaid", "open", "awaiting_payment", "cancelled", "expired", "overdue", "draft"];
  const deleteInvoice = async (inv) => {
    if (!window.confirm(lang === "de"
      ? "Möchten Sie diese unbezahlte Rechnung wirklich löschen? Der zugehörige offene Auftrag wird ebenfalls storniert."
      : "Delete this unpaid invoice? The related open order will also be cancelled.")) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success(lang === "de" ? "Rechnung gelöscht" : "Invoice deleted");
      setItems((prev) => prev.filter((x) => x.id !== inv.id));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const downloadInvoice = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${inv.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/files", { name: file.name, mime: file.type, size: file.size, data: reader.result });
        toast.success("OK"); load();
      } catch (err) { toast.error(apiErr(err)); }
    };
    reader.readAsDataURL(file);
  };
  const downloadFile = async (f) => {
    const { data } = await api.get(`/files/${f.id}/download`);
    const a = document.createElement("a"); a.href = data.data; a.download = data.name; a.click();
  };
  const delFile = async (id) => { await api.delete(`/files/${id}`); load(); };

  return (
    <div className="min-w-0 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-black">{title}</h1>
        {type === "files" && (
          <>
            <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="file-input" />
            <button onClick={() => fileRef.current.click()} className="btn-primary !py-2 !px-4 text-sm" data-testid="file-upload-btn"><Upload size={16} /> Upload</button>
          </>
        )}
      </div>

      {items.length === 0 && <p className="text-muted-foreground surface p-8 text-center">{t("common.none")}</p>}

      {type === "projects" && (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((p) => (
            <Link key={p.id} to={`/portal/projects/${p.id}`} className="surface p-6 hover:border-primary transition-colors" data-testid={`project-${p.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${badge(p.status)}`}>{stLabel(p.status, lang === "de")}</span>
                <span className="text-sm text-muted-foreground">{p.progress}%</span>
              </div>
              <h3 className="font-display font-bold text-lg">{p.name}</h3>
              <div className="h-2 rounded-full bg-secondary mt-4 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${p.progress}%` }} /></div>
            </Link>
          ))}
        </div>
      )}

      {type === "orders" && items.length > 0 && (
        <div className="space-y-4 md:hidden" data-testid="orders-mobile">
          {items.map((it) => (
            <article key={it.id} className="surface min-w-0 p-4" data-testid={`orders-card-${it.id}`}>
              <dl className="grid min-w-0 grid-cols-1 gap-4">
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">Nr.</dt>
                  <dd className="mt-1 font-mono text-sm [overflow-wrap:anywhere]">{it.number}</dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">{lang === "de" ? "Datum & Uhrzeit" : "Date & time"}</dt>
                  <dd className="mt-1 text-sm [overflow-wrap:anywhere]" data-testid={`order-mobile-date-${it.id}`}>{fmtDT(it.created_at)}</dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">{t("common.total")}</dt>
                  <dd className="mt-1 font-semibold [overflow-wrap:anywhere]">{(it.total || 0).toFixed(2)} €</dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">{t("common.status")}</dt>
                  <dd className="mt-1">
                    <span className={`inline-block max-w-full rounded-xl px-2.5 py-1 text-xs font-semibold capitalize [overflow-wrap:anywhere] ${badge(it.status)}`}>{stLabel(it.status, lang === "de")}</span>
                  </dd>
                </div>
              </dl>
              <Link to={`/portal/withdrawals?source=order&id=${encodeURIComponent(it.id)}`} className="btn-ghost mt-4 !py-2 text-sm">{lang === "de" ? "Vertrag widerrufen" : "Withdraw from contract"}</Link>
            </article>
          ))}
        </div>
      )}

      {(type === "offers" || type === "orders" || type === "invoices") && (
        <div className={`surface overflow-x-auto ${type === "orders" ? "hidden md:block" : ""}`}>
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-muted-foreground">
              <tr>
                <th className="p-4 font-semibold">Nr.</th>
                {type === "offers" && <th className="p-4 font-semibold">Titel</th>}
                {type === "orders" && <th className="p-4 font-semibold">{lang === "de" ? "Datum & Uhrzeit" : "Date & time"}</th>}
                <th className="p-4 font-semibold">{t("common.total")}</th>
                <th className="p-4 font-semibold">{t("common.status")}</th>
                <th className="p-4 font-semibold text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-border" data-testid={`${type}-row-${it.id}`}>
                  <td className="p-4 font-mono">{it.number}</td>
                  {type === "offers" && <td className="p-4">{it.title}</td>}
                  {type === "orders" && <td className="p-4 whitespace-nowrap text-xs" data-testid={`order-date-${it.id}`}>{fmtDT(it.created_at)}</td>}
                  <td className="p-4 font-semibold">{(it.total || 0).toFixed(2)} €</td>
                  <td className="p-4"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${badge(it.status)}`}>{stLabel(it.status, lang === "de")}</span></td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-2 justify-end">
                      {(type === "orders" || type === "invoices") && <Link to={`/portal/withdrawals?source=${type === "orders" ? "order" : "invoice"}&id=${encodeURIComponent(it.id)}`} className="btn-ghost !py-1.5 !px-3 text-xs">{lang === "de" ? "Vertrag widerrufen" : "Withdraw from contract"}</Link>}
                      {type === "offers" && it.status === "sent" && (
                        <>
                          <button onClick={() => respond(it.id, "accepted")} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`accept-${it.id}`}><Check size={14} /> {t("portal.accept")}</button>
                          <button onClick={() => respond(it.id, "rejected")} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`reject-${it.id}`}><X size={14} /></button>
                        </>
                      )}
                      {type === "invoices" && (
                        <>
                          <button onClick={() => downloadInvoice(it)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`invoice-pdf-${it.id}`}><Download size={14} /> PDF</button>
                          {(it.status === "unpaid" || it.status === "awaiting_payment") && <button onClick={() => pay(it)} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`invoice-pay-${it.id}`}><CreditCard size={14} /> {t("portal.pay")}</button>}
                          {DELETABLE.includes((it.status || "").toLowerCase()) && <button onClick={() => deleteInvoice(it)} className="btn-ghost !py-1.5 !px-3 text-xs text-red-500 hover:text-red-600" data-testid={`invoice-delete-${it.id}`}><Trash2 size={14} /> {lang === "de" ? "Löschen" : "Delete"}</button>}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {type === "files" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((f) => (
            <div key={f.id} className="surface p-5 flex items-center justify-between" data-testid={`file-${f.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={20} className="text-primary shrink-0" />
                <div className="min-w-0"><div className="font-medium truncate">{f.name}</div><div className="text-xs text-muted-foreground">{Math.round((f.size || 0) / 1024)} KB</div></div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => downloadFile(f)} className="p-2 hover:text-primary"><Download size={16} /></button>
                <button onClick={() => delFile(f.id)} className="p-2 hover:text-destructive"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {checkoutInv && (
        <CheckoutDialog invoice={checkoutInv} onClose={() => setCheckoutInv(null)} onPaid={load} />
      )}
    </div>
  );
}

